import { memo } from 'react';
import { useTheme } from '@simplywallst/ui-core';
import { formatMultiple, getLabelWidth } from '../../../utils';

interface Props {
  'data-cy-id'?: string;
  title: string;
  multiple: number | null;
  fill: string;
  fontColor?: string;
  xOffset?: number;
  x: number;
  y: number;
  height: number;
  chartWidth: number;
  lineWidth?: number;
  letterWidth?: number;
  anchor?: 'end' | 'start';
  condensed?: boolean;
  showMultiple?: boolean;
}

const Label = memo(
  ({
    title,
    multiple,
    fill,
    fontColor,
    x = 0,
    y = 0,
    anchor = 'end',
    height,
    lineWidth = 1,
    letterWidth = 8,
    chartWidth,
    xOffset = 0,
    condensed,
    'data-cy-id': dataCyId,
    showMultiple = true,
  }: Props) => {
    const theme = useTheme();
    const reverseLabel = anchor === 'start';
    const multipleFormatted = showMultiple ? formatMultiple(multiple) : '';

    //Figure out approximate length of label
    const width = getLabelWidth(
      condensed,
      multipleFormatted.length,
      title.length,
      8,
      letterWidth
    );
    const additionalHeight = condensed ? 16 : 0;

    //Clamp label x value to be within view
    const clampedX = reverseLabel
      ? Math.min(x, chartWidth - width)
      : Math.max(x, width);

    //Clamp line x values
    const clampedLineX = Math.min(Math.max(22, x), chartWidth - lineWidth - 16);

    return (
      <g overflow="visible" data-cy-id={dataCyId}>
        {/* Line */}
        <rect
          height={height}
          width={lineWidth}
          fill={fill}
          transform={`translate(${clampedLineX},${y})`}
        />
        <g transform={`translate(${reverseLabel ? 0 : lineWidth - 2},-19)`}>
          {/* Triangle shape */}
          <g transform={`translate(${clampedLineX},${y})`}>
            <polygon
              points={`0 0,11 0,11 19,${11 - lineWidth} 19,0 0`}
              overflow="visible"
              transform={`translate(${reverseLabel ? 11 : -9} 0) ${
                reverseLabel ? 'scale(-1, 1)' : ''
              }`}
              fill={fill}
            />
          </g>
          <g transform={`translate(${clampedX},${y})`}>
            {/* Label */}
            <rect
              width={width}
              height={25 + additionalHeight}
              fill={fill}
              rx={2}
              transform={`translate(${
                (reverseLabel ? 0 : -width + 2) + xOffset
              } ${-17 - additionalHeight})`}
            />
            {/* Label text */}
            <text
              fill={fontColor || theme.color.white}
              fontSize={13}
              transform={`translate(${
                (reverseLabel ? 9 : 9 - width) + xOffset
              } ${-additionalHeight})`}
            >
              {`${title}${condensed ? '' : ' ' + multipleFormatted}`}
            </text>
            {condensed && (
              <text
                fill={fontColor || theme.color.white}
                fontSize={13}
                transform={`translate(${
                  (reverseLabel ? 9 : 9 - width) + xOffset
                } 0)`}
              >
                {multipleFormatted}
              </text>
            )}
          </g>
        </g>
      </g>
    );
  }
);

export default Label;
